import { useEffect, useState } from "react"
import type { ReactNode } from "react"
import { Box, HStack, Icon, Input, chakra } from "@chakra-ui/react"
import { FaChevronDown, FaChevronUp } from "react-icons/fa6"
import { MUTED, USER_DEFINED } from "@hydrologiq/shared/components/alerts/alertSummary"

// The design's value box (v5): room for four digits beside the up and down arrows
const UNIT_INPUT_WIDTH = 170
const UNIT_WIDTH = "70px"
// A condition's field and comparison boxes: one size locked and editing, so a card keeps
// its shape when Edit opens
export const FIELD_WIDTH = "300px"
// How far a condition's field box gives way on a narrow card, so the row narrows before the
// conditions scroll sideways and push the AND/OR chips out of view
export const FIELD_MIN_WIDTH = "180px"
export const COMPARISON_WIDTH = "160px"
export const CONDITION_HEIGHT = "40px"
// The design's locked boxes (v5): white fill, --border-subtle edge, --fg-2 text
const LOCKED_BG = "white"
const LOCKED_BORDER = "#EEEEEE"
// The unit segment's grey, locked or not
const UNIT_BG = "#EBEBEB"
const UNIT_DIVIDER = "#D0D0D0"

/** A number with a fixed unit segment. Commits on blur or Enter; Escape restores the value. */
export function UnitInput({
  value,
  unit,
  ariaLabel,
  disabled,
  invalid,
  tone = "neutral",
  onChange,
  onCommit,
}: {
  value: string
  unit: string
  ariaLabel: string
  disabled?: boolean
  invalid?: boolean
  tone?: "neutral" | "userDefined"
  /** Controlled use (the draft row); otherwise the input keeps a local draft */
  onChange?: (value: string) => void
  onCommit?: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const shown = onChange ? value : draft
  const userDefined = tone === "userDefined"
  // Locked: the same grey as the field and comparison boxes beside it
  const locked = !!disabled

  const commit = () => {
    if (!onChange && draft !== value) onCommit?.(draft)
  }

  /** One up or down, from the value shown (an empty box counts as 0). */
  const stepBy = (delta: number) => {
    const current = Number(shown)
    const base = shown.trim() === "" || !Number.isFinite(current) ? 0 : current
    // Rounded, so 0.1 + 1 doesn't show as 1.1000000000000001
    const next = String(Math.round((base + delta) * 1e6) / 1e6)
    if (onChange) {
      onChange(next)
    } else {
      setDraft(next)
      onCommit?.(next)
    }
  }

  return (
    <HStack
      gap={0}
      w={`${UNIT_INPUT_WIDTH}px`}
      h="40px"
      flexShrink={0}
      align="stretch"
      // A customised value keeps the design's purple, locked or not
      borderWidth={userDefined ? "2px" : "1.5px"}
      borderColor={invalid ? "#E53E3E" : userDefined ? USER_DEFINED : locked ? LOCKED_BORDER : "#D0D0D0"}
      borderRadius="5px"
      bg={locked ? LOCKED_BG : "white"}
      overflow="hidden"
      _focusWithin={{ borderColor: "#39aed2" }}
    >
      {locked ? (
        // Not an <input>: the theme's global `input { bg: white !important }` sits in an
        // earlier cascade layer, so no override could grey a disabled one. Read like one.
        <Box
          role="spinbutton"
          aria-label={ariaLabel}
          aria-valuenow={shown === "" ? undefined : Number(shown)}
          aria-valuetext={shown === "" ? "—" : shown}
          aria-disabled="true"
          aria-readonly="true"
          flex={1}
          minW={0}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          px="10px"
          fontSize="md"
          fontWeight="semibold"
          // Grey even when customised: the purple border and unit say so
          color={MUTED}
          cursor="default"
          truncate
        >
          {shown === "" ? "—" : shown}
        </Box>
      ) : (
        <Input
          type="number"
          inputMode="decimal"
          step="any"
          value={shown}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          onChange={(e) => (onChange ? onChange(e.target.value) : setDraft(e.target.value))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit()
            if (e.key === "Escape") setDraft(value)
          }}
          flex={1}
          w={0}
          h="100%"
          border="none"
          borderRadius={0}
          outline="none"
          focusRing="none"
          textAlign="right"
          fontSize="md"
          fontWeight="semibold"
          pl="10px"
          pr="6px"
          // Our own arrows below: the browser's can't be sized, and sit over the number
          css={{
            appearance: "textfield",
            "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": { appearance: "none", margin: 0 },
          }}
        />
      )}
      {!locked && (
        <Box display="flex" flexDirection="column" w="22px" flexShrink={0} py="3px" pr="2px">
          <StepButton label={`Increase ${ariaLabel}`} icon={FaChevronUp} onClick={() => stepBy(1)} />
          <StepButton label={`Decrease ${ariaLabel}`} icon={FaChevronDown} onClick={() => stepBy(-1)} />
        </Box>
      )}
      <Box
        w={UNIT_WIDTH}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="sm"
        color={userDefined ? USER_DEFINED : MUTED}
        bg={userDefined ? "rgba(161,112,217,.22)" : UNIT_BG}
        borderLeft="1px solid"
        borderColor={userDefined ? "rgba(161,112,217,.45)" : UNIT_DIVIDER}
      >
        {unit}
      </Box>
    </HStack>
  )
}

/**
 * A value that can't be changed here (a field or a comparison), in an input's footprint. With
 * `minW` it narrows down to that on a narrow card.
 */
export function LockedBox({ w, minW, children }: { w: string; minW?: string; children: ReactNode }) {
  return (
    <Box
      w={w}
      minW={minW}
      h="40px"
      flexShrink={minW ? 1 : 0}
      display="flex"
      alignItems="center"
      px={3}
      fontSize="md"
      color={MUTED}
      bg={LOCKED_BG}
      border={`1.5px solid ${LOCKED_BORDER}`}
      borderRadius="5px"
      truncate
    >
      {children}
    </Box>
  )
}

function StepButton({ label, icon, onClick }: { label: string; icon: typeof FaChevronUp; onClick: () => void }) {
  return (
    <chakra.button
      type="button"
      aria-label={label}
      // The keyboard's arrow keys already step the value
      tabIndex={-1}
      onClick={onClick}
      flex={1}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={MUTED}
      borderRadius="3px"
      cursor="pointer"
      _hover={{ color: "#39aed2", bg: "#EAF7FA" }}
    >
      <Icon as={icon} boxSize="12px" />
    </chakra.button>
  )
}
